import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Search, X, Brain, HeartPulse, Hand, Footprints, ChevronDown } from "lucide-react";
import BodyModel from "@/components/clinic/BodyModel";
import ClinicNarrator from "@/components/clinic/ClinicNarrator";
import UrgencyQuiz from "@/components/clinic/UrgencyQuiz";
import SecondaryDeck from "@/components/clinic/SecondaryDeck";
import { REGIONS, byRegion, FINDER, BANDS } from "@/lib/clinicData";
import useProgress from "@/lib/useProgress";
import { PLATFORMS } from "@/lib/platforms";

const ICONS = { head: Brain, torso: HeartPulse, arm_left: Hand, arm_right: Hand, leg_left: Footprints, leg_right: Footprints };

function useNarrow() {
  const [n, setN] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const on = () => setN(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return n;
}

const SHORT = { emergency: "Emergency", urgent: "Today", soon: "This week", watch: "Watch it" };

export default function ClinicDesk() {
  const [picked, setPicked] = useState(null);
  const [finder, setFinder] = useState(false);
  const [q, setQ] = useState("");
  const [data, patch] = useProgress("clinic", { logs: [] });
  const narrow = useNarrow();
  const logs = data.logs || [];

  const results = useMemo(() => {
    if (!q.trim()) return FINDER;
    const t = q.toLowerCase();
    return FINDER.map((c) => ({ ...c, items: c.items.filter((i) => i.toLowerCase().includes(t)) })).filter((c) => c.items.length);
  }, [q]);

  const addLog = (entry) => {
    patch((d) => ({ logs: [{ id: `l${Date.now()}`, date: new Date().toISOString().slice(0, 10), ...entry }, ...(d.logs || [])] }));
    toast("Kept in your log.");
  };

  const pickRegion = (id) => { setPicked(byRegion(id)); setFinder(false); };

  const quizPanel = picked ? (
    <UrgencyQuiz region={picked} onClose={() => setPicked(null)} onLog={addLog} />
  ) : (
    <div>
      <p className="text-[10px] tracking-[0.22em] uppercase text-white/35">How this works</p>
      <h2 className="font-display text-2xl text-white mt-2 leading-tight">Nothing selected yet</h2>
      <div className="mt-4">
        <ClinicNarrator />
      </div>
      <p className="text-[10px] tracking-[0.22em] uppercase text-white/35 mt-6 mb-2">The four bands</p>
      <div className="space-y-1.5">
        {Object.values(BANDS).map((b, i) => (
          <motion.div key={b.key} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }}
            className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ background: "rgba(255,255,255,0.04)" }}>
            <span className="w-2 h-2 rounded-full" style={{ background: b.tone }} />
            <span className="text-sm text-white/80 font-medium">{SHORT[b.key]}</span>
            <span className="text-[10px] text-white/35 ml-auto">{b.window}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );

  return (
    <main className="pt-16" data-testid="platform-page-health">
      {/* =============================================== the stage */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(165deg, #1B0806 0%, #3A0F0A 45%, #240A07 100%)" }} data-testid="clinic-stage">
        <div className="absolute top-[-15%] left-1/2 -translate-x-1/2 w-[70vw] h-[60vh] rounded-full blur-[130px] opacity-[0.26] drift" style={{ background: "radial-gradient(circle, #FF5B39, transparent 70%)" }} />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40vw] h-[40vh] rounded-full blur-[130px] opacity-[0.18]" style={{ background: "radial-gradient(circle, #FF8A5B, transparent 70%)" }} />

        <div className="relative max-w-[1600px] mx-auto px-5 sm:px-8 pt-8 pb-6 grain">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            {/* ---- left ---- */}
            <div className="lg:col-span-3 order-2 lg:order-none">
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] font-bold tracking-[0.14em] uppercase" style={{ background: "#E2604A", color: "#fff" }}>
                Clinic Desk
              </span>
              <h1 className="font-display text-4xl sm:text-5xl text-white mt-4 leading-[0.94]">
                Show me<br /><span style={{ color: "#FFA98F" }}>where</span>
              </h1>

              <p className="text-sm text-white/50 mt-4 max-w-xs">Spin the figure and tap the part that is wrong, or pick it from the list.</p>

              {/* six regions */}
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/30 mt-7 mb-2">Six parts</p>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-1.5">
                {REGIONS.map((r) => {
                  const Icon = ICONS[r.id];
                  const on = picked?.id === r.id;
                  return (
                    <button
                      key={r.id}
                      onClick={() => pickRegion(r.id)}
                      className="group flex items-center gap-2.5 rounded-2xl px-3.5 py-2.5 text-left transition-colors"
                      style={on ? { background: "#E2604A" } : { background: "rgba(255,255,255,0.04)" }}
                      data-testid={`region-chip-${r.id}`}
                    >
                      <Icon size={14} style={{ color: on ? "#fff" : "#FFA98F" }} className="shrink-0" />
                      <span className={`text-sm font-medium ${on ? "text-white" : "text-white/75"}`}>{r.label}</span>
                    </button>
                  );
                })}
              </div>

              <button onClick={() => setFinder(true)} className="mt-3 w-full flex items-center justify-between rounded-2xl px-3.5 py-2.5 text-sm transition-colors" style={{ background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.6)" }} data-testid="finder-open">
                <span className="inline-flex items-center gap-2"><Search size={13} /> Find it by name</span>
                <ChevronDown size={13} />
              </button>
              <p className="text-[10px] text-white/25 mt-3">No diagnosis. No account needed. Nothing leaves this screen unless you keep it.</p>
            </div>

            {/* ---- centre: the body ---- */}
            <div className="lg:col-span-5 order-1 lg:order-none">
              <div className="h-[56vh] sm:h-[64vh] lg:h-[82vh] rounded-[2rem] relative overflow-hidden"
                style={{ background: "radial-gradient(55% 55% at 50% 40%, rgba(255,91,57,0.18), rgba(0,0,0,0) 72%)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <BodyModel picked={picked} onPick={pickRegion} />
              </div>
            </div>

            {/* ---- right: the read (desktop) ---- */}
            <div className="hidden lg:block lg:col-span-4">
              <div className="rounded-[2rem] p-5 sm:p-6 lg:sticky lg:top-24" style={{ background: "rgba(255,255,255,0.045)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(18px)" }} data-testid="clinic-read-panel">
                <AnimatePresence mode="wait">
                  <motion.div key={picked?.id || "empty"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                    {quizPanel}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        <div className="relative border-t" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-3.5 flex flex-wrap items-center gap-x-5 gap-y-1">
            <p className="text-[10px] tracking-[0.18em] uppercase text-white/30">If it is on the red flag board</p>
            <p className="text-sm text-white/60">Emergency services, 112. Do not wait for a read to agree with you.</p>
          </div>
        </div>
      </section>

      {/* mobile sheet with the read */}
      <AnimatePresence>
        {narrow && picked && (
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed left-0 right-0 bottom-0 z-[75] max-h-[82vh] overflow-y-auto rounded-t-[1.8rem] p-5 pb-8"
            style={{ background: "linear-gradient(180deg, #2A0C08, #1B0806)", borderTop: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 -20px 60px rgba(0,0,0,0.6)" }}
            data-testid="clinic-read-sheet"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="w-10 h-1 rounded-full" style={{ background: "rgba(255,255,255,0.2)" }} />
              <button onClick={() => setPicked(null)} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.1)" }} data-testid="sheet-close"><X size={14} className="text-white/70" /></button>
            </div>
            <UrgencyQuiz region={picked} onClose={() => setPicked(null)} onLog={addLog} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* the finder overlay */}
      <AnimatePresence>
        {finder && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setFinder(false)} className="fixed inset-0 z-[88]" style={{ background: "rgba(20,5,3,0.6)", backdropFilter: "blur(8px)" }} />
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }}
              className="fixed z-[89] left-1/2 -translate-x-1/2 top-[12vh] w-[92vw] max-w-2xl rounded-[1.6rem] overflow-hidden"
              style={{ background: "linear-gradient(180deg, #2E0D09, #1B0806)", border: "1px solid rgba(255,255,255,0.12)" }}
              data-testid="finder-panel"
            >
              <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                <Search size={15} className="text-white/40" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Headache, chest pressure, swollen ankle" className="flex-1 bg-transparent text-base text-white outline-none placeholder:text-white/30" data-testid="finder-input" />
                <button onClick={() => setFinder(false)} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)" }} data-testid="finder-close"><X size={14} className="text-white/60" /></button>
              </div>
              <div className="max-h-[58vh] overflow-y-auto p-5 space-y-5">
                {results.map((c) => (
                  <div key={c.cat}>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-white/30 mb-2">{c.cat}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {c.items.map((it) => (
                        <button key={it} onClick={() => pickRegion(c.region)} className="rounded-full px-3 py-1.5 text-[13px] transition-colors" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.75)" }} data-testid={`finder-item-${it}`}>
                          {it}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {results.length === 0 && <p className="text-sm text-white/40">Nothing under that word. Pick the part of the body instead.</p>}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* =============================================== below */}
      <SecondaryDeck logs={logs} onAdd={(e) => patch((d) => ({ logs: [{ ...e }, ...(d.logs || [])] }))} onRemove={(id) => patch((d) => ({ logs: (d.logs || []).filter((l) => l.id !== id) }))} />

      <section style={{ background: "#F6E7DF", borderTop: "1px solid rgba(58,24,16,0.1)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
          <p className="kicker mb-4" style={{ color: "#A67C70" }}>The other doors</p>
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.filter((x) => x.key !== "health").map((x) => (
              <Link key={x.key} to={x.path} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5" style={{ background: "#fff", border: "1px solid rgba(58,24,16,0.1)", color: "#3A1810" }} data-testid={`door-${x.key}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: x.hue }} />
                {x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
